<template>
  <div class="pending-submission-page">
    <Head title="Pending Submissions" />

    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.14em] text-brand-500">Operations</p>
        <h1 class="mt-1 text-3xl font-bold tracking-tight text-brand-900">Pending Submissions</h1>
        <p class="mt-1.5 text-sm text-gray-500">
          Pre-LOS holds: tech errors, slow Pefindo, and admin retry before LOS submit
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 shadow-sm transition hover:bg-gray-50"
          @click="reset"
        >
          Reset
        </button>
        <button
          type="button"
          class="inline-flex items-center rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700"
          @click="applyFilters"
        >
          Apply filters
        </button>
      </div>
    </div>

    <div class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div class="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Actionable</p>
        <p class="mt-1 text-xl font-bold text-brand-900">{{ formatNumber(summary.actionable || 0) }}</p>
      </div>
      <div class="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Waiting Pefindo</p>
        <p class="mt-1 text-xl font-bold text-amber-900">{{ formatNumber(summary.pefindo_pending || 0) }}</p>
      </div>
      <div class="rounded-2xl border border-teal-200 bg-teal-50 px-4 py-3 shadow-sm">
        <p class="text-[11px] font-semibold uppercase tracking-wide text-teal-700">Ready for LOS</p>
        <p class="mt-1 text-xl font-bold text-teal-900">{{ formatNumber(summary.ready_for_los || 0) }}</p>
      </div>
    </div>

    <div class="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <div class="mb-4">
        <h2 class="text-sm font-bold text-brand-900">Filters</h2>
        <p class="text-xs text-gray-500">Search by customer, filter status/reason, and set page size</p>
      </div>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Search</label>
          <input
            v-model="form.search"
            type="text"
            placeholder="NIK, name, phone, event_id..."
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
            @keyup.enter="applyFilters"
          />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Status</label>
          <select
            v-model="form.status"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="">Actionable (default)</option>
            <option value="pending">Pending</option>
            <option value="ready_for_los">Ready for LOS</option>
            <option value="submitted">Submitted</option>
            <option value="already_in_los">Already in LOS</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Reason</label>
          <select
            v-model="form.reason"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="">All reasons</option>
            <option value="PEFINDO_PENDING">Slow / pending Pefindo</option>
            <option value="PRE_LIMIT_ERROR">Pre-limit error</option>
            <option value="LIMIT_ERROR">Limit error</option>
            <option value="AIRPACK_PENDING">Airpack pending</option>
            <option value="LOS_SUBMIT_UNCERTAIN">LOS submit uncertain</option>
          </select>
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Retries</label>
          <select
            v-model="form.retries"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="">Any</option>
            <option value="0">0</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="1plus">1+</option>
            <option value="3plus">3+</option>
          </select>
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Error / reject</label>
          <select
            v-model="form.error_type"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option value="">All</option>
            <option value="WAITING_PEFINDO">Waiting Pefindo</option>
            <option value="P00">Reject P00</option>
            <option value="OKI001">OKI001 / MS 금리 Airpack</option>
          </select>
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-semibold text-gray-600">Per page</label>
          <select
            v-model="form.paging"
            class="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
          >
            <option :value="10">10</option>
            <option :value="15">15</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
          </select>
        </div>
      </div>
    </div>

    <div
      v-if="busyId"
      class="mb-5 overflow-hidden rounded-2xl border border-amber-200 bg-amber-50 shadow-sm"
      role="status"
      aria-live="polite"
    >
      <div class="px-5 py-4">
        <div class="flex items-start gap-3">
          <span
            class="mt-0.5 inline-block h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-amber-300 border-t-amber-700"
          />
          <div class="min-w-0 flex-1">
            <p class="text-sm font-bold text-amber-950">
              {{ progressTitle }}
            </p>
            <p class="mt-1 text-sm text-amber-900">
              {{ progressStepLabel }}
            </p>
            <p v-if="busyCustomer" class="mt-1 truncate text-xs text-amber-800">
              {{ busyCustomer }}
            </p>
            <div class="mt-3 h-1.5 overflow-hidden rounded-full bg-amber-200">
              <div
                class="h-full rounded-full bg-amber-600 transition-all duration-500"
                :style="{ width: progressPercent + '%' }"
              />
            </div>
            <ol class="mt-3 space-y-1">
              <li
                v-for="(step, idx) in progressSteps"
                :key="step"
                class="flex items-center gap-2 text-xs"
                :class="idx < progressStepIndex ? 'text-amber-800' : idx === progressStepIndex ? 'font-semibold text-amber-950' : 'text-amber-700/60'"
              >
                <span class="w-4 text-center">
                  <template v-if="idx < progressStepIndex">✓</template>
                  <template v-else-if="idx === progressStepIndex">›</template>
                  <template v-else>·</template>
                </span>
                <span>{{ step }}</span>
              </li>
            </ol>
            <p class="mt-3 text-[11px] text-amber-800/80">
              {{ isBulkAction
                ? `Processing one row at a time (${bulkDone} of ${bulkTotal}). Do not close this page.`
                : 'Please wait — Eagle / Airpack can take up to ~2 minutes. Do not close this page.' }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div class="flex flex-col gap-3 border-b border-gray-100 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 class="text-sm font-bold text-brand-900">Queue</h2>
          <p class="text-xs text-gray-500">
            Showing {{ (rows.data || []).length }} of {{ formatNumber(rows.total || 0) }}
          </p>
        </div>
        <div class="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-xl bg-amber-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            :disabled="selectedBulkPefindoCount === 0 || busyId !== null"
            @click="bulkSearchPefindo"
          >
            {{ busyAction === 'bulk-pefindo'
              ? `Pefindo ${bulkDone}/${bulkTotal}`
              : `Search Pefindo (${selectedBulkPefindoCount})` }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-xl bg-orange-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            :disabled="selectedBulkAirpackCount === 0 || busyId !== null"
            @click="bulkRetryAirpack"
          >
            {{ busyAction === 'bulk-airpack'
              ? `Airpack ${bulkDone}/${bulkTotal}`
              : `Retry Airpack (${selectedBulkAirpackCount})` }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            :disabled="selectedBulkRescanCount === 0 || busyId !== null"
            @click="bulkRescanPolicy"
          >
            {{ busyAction === 'bulk-rescan'
              ? `P10–P40 ${bulkDone}/${bulkTotal}`
              : `Rescan P10–P40 (${selectedBulkRescanCount})` }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="selectedBulkLosCount === 0 || busyId !== null"
            @click="bulkCheckLos"
          >
            {{ busyAction === 'bulk-check-los'
              ? `Check LOS ${bulkDone}/${bulkTotal}`
              : `Check LOS (${selectedBulkLosCount})` }}
          </button>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full">
          <thead>
            <tr class="border-b border-gray-100 bg-brand-50/60 text-left">
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">
                <input
                  type="checkbox"
                  class="rounded border-gray-300"
                  :checked="allBulkSelected"
                  :disabled="bulkCandidates.length === 0 || busyId !== null"
                  @change="toggleAllBulk($event.target.checked)"
                />
              </th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">#</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Customer</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Reason</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Status</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Pefindo</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Updated</th>
              <th class="whitespace-nowrap px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-brand-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in rows.data"
              :key="row.id"
              class="border-b border-gray-50 align-top transition-colors hover:bg-brand-50/40"
            >
              <td class="whitespace-nowrap px-4 py-3.5">
                <input
                  type="checkbox"
                  class="rounded border-gray-300"
                  :checked="selectedIds.includes(row.id)"
                  :disabled="!canBulkSelect(row) || busyId !== null"
                  @change="toggleBulk(row, $event.target.checked)"
                />
              </td>
              <td class="whitespace-nowrap px-4 py-3.5 text-sm font-semibold text-gray-500">
                {{ rowNumber(index) }}
              </td>
              <td class="px-4 py-3.5 text-sm text-gray-700">
                <div class="font-semibold text-brand-900">{{ row.name || '—' }}</div>
                <div class="mt-0.5 font-mono text-xs text-gray-500">{{ row.nik || '—' }}</div>
                <div class="text-xs text-gray-500">{{ row.phone_num || '—' }}</div>
                <div v-if="row.appc_no" class="mt-1 text-xs font-medium text-teal-700">
                  Appc: {{ row.appc_no }}
                </div>
              </td>
              <td class="px-4 py-3.5 text-sm text-gray-700">
                <span
                  class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold"
                  :class="categoryBadgeClass(row)"
                >
                  {{ categoryLabel(row) }}
                </span>
                <div
                  v-if="row.last_error"
                  class="mt-2 max-w-xs whitespace-normal break-words text-xs"
                  :class="isApprovePass(row) ? 'text-amber-700' : 'text-red-600'"
                >
                  {{ row.last_error }}
                </div>
              </td>
              <td class="px-4 py-3.5 text-sm text-gray-700">
                <span
                  class="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold"
                  :class="statusClass(row.status)"
                >
                  {{ statusLabel(row.status) }}
                </span>
              </td>
              <td class="px-4 py-3.5 text-sm text-gray-700">
                <div class="text-xs">
                  <span class="font-semibold text-gray-600">Pefindo:</span>
                  {{ row.pefindo_status || '—' }}
                </div>
                <div class="mt-1 text-xs">
                  <span class="font-semibold text-gray-600">Airpack:</span>
                  {{ row.airpack_status || '—' }}
                </div>
                <div class="mt-1 max-w-[180px] break-all font-mono text-[11px] text-gray-500" :title="row.event_id || ''">
                  {{ row.event_id || 'No event_id' }}
                </div>
                <div class="mt-1 text-xs text-gray-500">Retries: {{ row.retry_count || 0 }}</div>
              </td>
              <td class="whitespace-nowrap px-4 py-3.5 text-sm text-gray-600">
                {{ formatDate(row.updated_at) }}
              </td>
              <td class="px-4 py-3.5">
                <div class="flex min-w-[140px] flex-col gap-1.5">
                  <button
                    type="button"
                    class="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    :disabled="busyId !== null"
                    @click="startAction(row, 'check-los', `/pending-submissions/${row.id}/check-los`)"
                  >
                    {{ busyId === row.id && busyAction === 'check-los' ? 'Checking…' : 'Check LOS' }}
                  </button>
                  <button
                    type="button"
                    class="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                    :class="canRetryPefindo(row) ? 'bg-amber-600 hover:bg-amber-700' : 'bg-gray-300'"
                    :disabled="!canRetryPefindo(row) || busyId !== null"
                    @click="startRetry(row)"
                  >
                    {{ busyId === row.id && (busyAction === 'retry-airpack' || busyAction === 'retry-pefindo')
                      ? 'Working…'
                      : retryButtonLabel(row) }}
                  </button>
                  <button
                    type="button"
                    class="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                    :class="canRescanPolicy(row) ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-300'"
                    :disabled="!canRescanPolicy(row) || busyId !== null"
                    @click="startAction(row, 'rescan-policy', `/pending-submissions/${row.id}/rescan-policy`)"
                  >
                    {{ busyId === row.id && busyAction === 'rescan-policy'
                      ? 'Scanning…'
                      : 'Rescan P10–P40' }}
                  </button>
                  <button
                    type="button"
                    class="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                    :class="canSubmitLos(row) ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-300'"
                    :disabled="!canSubmitLos(row) || busyId !== null"
                    @click="openSubmitPreview(row)"
                  >
                    {{ busyId === row.id && busyAction === 'submit-los'
                      ? 'Submitting…'
                      : submitLosLabel(row) }}
                  </button>
                  <button
                    type="button"
                    class="rounded-lg border border-red-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                    :disabled="busyId !== null || ['submitted', 'dismissed', 'already_in_los'].includes(row.status)"
                    @click="confirmDismiss(row)"
                  >
                    {{ busyId === row.id && busyAction === 'dismiss' ? 'Dismissing…' : 'Dismiss' }}
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="!rows.data || rows.data.length === 0">
              <td colspan="8" class="px-4 py-16 text-center">
                <p class="text-base font-bold text-brand-900">No pending submissions</p>
                <p class="mt-1 text-sm text-gray-500">
                  Nothing in this queue, or try clearing filters.
                </p>
                <button
                  type="button"
                  class="mt-4 rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
                  @click="reset"
                >
                  Clear filters
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="border-t border-gray-100 bg-gray-50 px-5 py-3">
        <pagination class="mt-0" :links="rows.links || []" preserve-scroll />
      </div>
    </div>

    <div
      v-if="submitPreviewOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      @click.self="closeSubmitPreview"
    >
      <div class="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl">
        <div class="border-b border-gray-100 px-5 py-4">
          <h3 class="text-base font-bold text-brand-900">Confirm LOS submission</h3>
          <p class="mt-1 text-sm text-gray-500">
            Review the body that will be sent to ktamid / LOS before confirming.
          </p>
        </div>
        <div class="max-h-[60vh] overflow-y-auto px-5 py-4">
          <p v-if="submitPreviewLoading" class="text-sm text-gray-600">Loading preview…</p>
          <p v-else-if="submitPreviewError" class="text-sm text-red-700">{{ submitPreviewError }}</p>
          <template v-else-if="submitPreview">
            <div class="mb-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
              <div>
                <span class="font-semibold text-gray-600">Customer:</span>
                {{ submitPreview.customer?.name || '—' }}
              </div>
              <div>
                <span class="font-semibold text-gray-600">NIK:</span>
                {{ submitPreview.customer?.nik || '—' }}
              </div>
              <div class="sm:col-span-2">
                <span class="font-semibold text-gray-600">Target:</span>
                <span class="break-all font-mono text-xs text-gray-700">{{ submitPreview.target || '—' }}</span>
              </div>
              <div v-if="submitPreview.is_reject" class="sm:col-span-2">
                <span class="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-800">
                  Rejection submit
                  <template v-if="submitPreview.form?.rejectCode">
                    ({{ submitPreview.form.rejectCode }})
                  </template>
                </span>
              </div>
            </div>
            <dl class="divide-y divide-gray-100 rounded-xl border border-gray-200">
              <div
                v-for="entry in submitPreviewEntries"
                :key="entry.key"
                class="grid grid-cols-1 gap-1 px-3 py-2.5 sm:grid-cols-[160px_1fr] sm:gap-3"
              >
                <dt class="font-mono text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  {{ entry.key }}
                </dt>
                <dd class="break-all text-sm text-gray-900">
                  {{ entry.value === '' || entry.value === null || entry.value === undefined ? '—' : entry.value }}
                </dd>
              </div>
            </dl>
          </template>
        </div>
        <div class="flex flex-wrap justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
          <button
            type="button"
            class="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            :disabled="busyId !== null"
            @click="closeSubmitPreview"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            :disabled="!submitPreview || submitPreviewLoading || !!submitPreviewError || busyId !== null"
            @click="confirmSubmitLos"
          >
            {{ busyAction === 'submit-los' ? 'Submitting…' : 'Confirm submit to LOS' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import { Inertia } from '@inertiajs/inertia'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import moment from 'moment'

export default {
  components: {
    Head,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    rows: Object,
    summary: {
      type: Object,
      default: () => ({
        actionable: 0,
        pefindo_pending: 0,
        ready_for_los: 0,
      }),
    },
  },
  data() {
    return {
      busyId: null,
      busyAction: null,
      busyCustomer: '',
      progressStepIndex: 0,
      progressTimer: null,
      selectedIds: [],
      bulkDone: 0,
      bulkTotal: 0,
      submitPreviewOpen: false,
      submitPreviewLoading: false,
      submitPreviewError: '',
      submitPreview: null,
      submitPreviewRow: null,
      form: {
        search: this.filters.search || '',
        status: this.filters.status || '',
        reason: this.filters.reason || '',
        retries: this.filters.retries || '',
        error_type: this.filters.error_type || '',
        paging: this.filters.paging || 15,
      },
    }
  },
  computed: {
    progressSteps() {
      const map = {
        'retry-airpack': [
          'Using saved Pefindo result',
          'Calling Airpack (EAI)',
          'Waiting for Airpack response',
          'Updating pending row',
        ],
        'retry-pefindo': [
          'Preparing Get Report retry',
          'Calling Eagle Get Report',
          'Waiting for report / Airpack',
          'Saving Pefindo + Airpack results',
        ],
        'rescan-policy': [
          'Reading saved Pefindo result',
          'Scanning for P10–P40 rejects',
          'Updating pending row',
        ],
        'bulk-pefindo': [
          'Searching Eagle for the selected row',
          'Waiting for the report',
          'Saving the result',
        ],
        'bulk-airpack': [
          'Using saved Pefindo result',
          'Calling Airpack / Eagle retry',
          'Updating pending row',
        ],
        'bulk-rescan': [
          'Reading saved Pefindo result',
          'Scanning for P10–P40 rejects',
          'Updating pending row',
        ],
        'bulk-check-los': [
          'Looking up NIK in LOS / local submissions',
        ],
        'submit-los': [
          'Building LOS payload',
          'Submitting to LOS via go_mid',
          'Confirming submission',
        ],
        'check-los': [
          'Looking up NIK in LOS / local submissions',
        ],
        dismiss: [
          'Dismissing pending row',
        ],
      }
      return map[this.busyAction] || ['Working…']
    },
    progressStepLabel() {
      return this.progressSteps[this.progressStepIndex] || this.progressSteps[0] || 'Working…'
    },
    progressTitle() {
      const map = {
        'retry-airpack': 'Retrying Airpack',
        'retry-pefindo': 'Retrying Pefindo',
        'rescan-policy': 'Rescanning P10–P40',
        'bulk-pefindo': 'Searching Pefindo',
        'bulk-airpack': 'Bulk Retry Airpack',
        'bulk-rescan': 'Bulk Rescan P10–P40',
        'bulk-check-los': 'Bulk Check LOS',
        'submit-los': 'Submitting to LOS',
        'check-los': 'Checking LOS',
        dismiss: 'Dismissing',
      }
      return map[this.busyAction] || 'Working'
    },
    isBulkAction() {
      return ['bulk-pefindo', 'bulk-airpack', 'bulk-rescan', 'bulk-check-los'].includes(this.busyAction)
    },
    bulkCandidates() {
      return (this.rows.data || []).filter((row) => this.canBulkSelect(row))
    },
    selectedBulkPefindoCount() {
      return (this.rows.data || []).filter((row) => this.selectedIds.includes(row.id) && this.canBulkPefindo(row)).length
    },
    selectedBulkAirpackCount() {
      return (this.rows.data || []).filter((row) => this.selectedIds.includes(row.id) && this.canBulkAirpack(row)).length
    },
    selectedBulkRescanCount() {
      return (this.rows.data || []).filter((row) => this.selectedIds.includes(row.id) && this.canRescanPolicy(row)).length
    },
    selectedBulkLosCount() {
      return (this.rows.data || []).filter((row) => this.selectedIds.includes(row.id) && this.canBulkCheckLos(row)).length
    },
    allBulkSelected() {
      return this.bulkCandidates.length > 0
        && this.bulkCandidates.every((row) => this.selectedIds.includes(row.id))
    },
    submitPreviewEntries() {
      const form = this.submitPreview && this.submitPreview.form
      if (!form || typeof form !== 'object') return []
      return Object.keys(form).map((key) => ({ key, value: form[key] }))
    },
    progressPercent() {
      const total = Math.max(this.progressSteps.length, 1)
      // Stay under 95% until the request finishes (real completion clears the panel).
      const raw = ((this.progressStepIndex + 1) / total) * 90
      return Math.min(90, Math.max(12, Math.round(raw)))
    },
  },
  beforeUnmount() {
    this.clearProgressTimer()
  },
  // Vue 2 Options API (Inertia vue3 may still use beforeDestroy in older builds)
  beforeDestroy() {
    this.clearProgressTimer()
  },
  methods: {
    applyFilters() {
      Inertia.get('/pending-submissions', this.form, { preserveState: true, replace: true })
    },
    reset() {
      this.form = {
        search: '',
        status: '',
        reason: '',
        retries: '',
        error_type: '',
        paging: 15,
      }
      this.applyFilters()
    },
    clearProgressTimer() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    startProgress(action, row) {
      this.clearProgressTimer()
      this.busyId = row.id
      this.busyAction = action
      this.busyCustomer = [row.name, row.nik].filter(Boolean).join(' · ')
      this.progressStepIndex = 0
      this.progressTimer = setInterval(() => {
        if (this.progressStepIndex < this.progressSteps.length - 1) {
          this.progressStepIndex += 1
        }
      }, 4500)
    },
    stopProgress() {
      this.clearProgressTimer()
      this.busyId = null
      this.busyAction = null
      this.busyCustomer = ''
      this.progressStepIndex = 0
    },
    startAction(row, action, url) {
      if (this.busyId !== null) return
      this.startProgress(action, row)
      Inertia.post(url, {}, {
        preserveScroll: true,
        onFinish: () => {
          this.stopProgress()
        },
      })
    },
    canBulkSelect(row) {
      return ['pending', 'ready_for_los'].includes(row.status)
    },
    canBulkPefindo(row) {
      // Eagle search / report-retry — not airpack-only failures (use Retry Airpack).
      if (row.pefindo_status === 'ready' && row.airpack_status === 'failed') return false
      return this.canRetryPefindo(row)
    },
    canBulkAirpack(row) {
      if (!['pending', 'ready_for_los'].includes(row.status)) return false
      // Includes OKI001 / MS 금리 — Airpack-only with saved Pefindo.
      return row.pefindo_status === 'ready' && row.airpack_status === 'failed'
    },
    canBulkCheckLos(row) {
      return this.canBulkSelect(row)
    },
    toggleBulk(row, checked) {
      if (!this.canBulkSelect(row)) return
      if (checked) {
        if (!this.selectedIds.includes(row.id)) this.selectedIds.push(row.id)
        return
      }
      this.selectedIds = this.selectedIds.filter((id) => id !== row.id)
    },
    toggleAllBulk(checked) {
      if (!checked) {
        const pageIds = this.bulkCandidates.map((row) => row.id)
        this.selectedIds = this.selectedIds.filter((id) => !pageIds.includes(id))
        return
      }
      const next = new Set(this.selectedIds)
      this.bulkCandidates.forEach((row) => next.add(row.id))
      this.selectedIds = Array.from(next)
    },
    xsrfToken() {
      const match = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]*)/)
      return match ? decodeURIComponent(match[1]) : ''
    },
    async runBulkAction({ action, label, urlForRow, filterFn, confirmText }) {
      const rows = (this.rows.data || []).filter((row) => this.selectedIds.includes(row.id) && filterFn(row))
      if (rows.length === 0 || this.busyId !== null) return
      if (!window.confirm(confirmText(rows.length))) return

      this.bulkTotal = rows.length
      this.bulkDone = 0
      this.busyAction = action
      const failures = []
      for (const row of rows) {
        this.startProgress(action, row)
        this.busyAction = action
        try {
          const response = await fetch(urlForRow(row), {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              'X-XSRF-TOKEN': this.xsrfToken(),
            },
            body: '{}',
          })
          const body = await response.json().catch(() => ({}))
          if (!response.ok || body.success === false) {
            failures.push(`${row.name || row.nik || row.id}: ${body.message || 'failed'}`)
          }
        } catch (error) {
          failures.push(`${row.name || row.nik || row.id}: ${error.message || 'failed'}`)
        }
        this.bulkDone += 1
      }
      this.stopProgress()
      this.selectedIds = []
      const summary = failures.length === 0
        ? `${label} finished for ${rows.length} row${rows.length === 1 ? '' : 's'}.`
        : `${label} finished. ${failures.length} of ${rows.length} failed.`
      window.alert(failures.length === 0 ? summary : `${summary}\n\n${failures.slice(0, 8).join('\n')}`)
      Inertia.reload({ preserveScroll: true })
    },
    bulkSearchPefindo() {
      return this.runBulkAction({
        action: 'bulk-pefindo',
        label: 'Pefindo search',
        urlForRow: (row) => `/pending-submissions/${row.id}/retry-pefindo`,
        filterFn: (row) => this.canBulkPefindo(row),
        confirmText: (n) => `Search / retry Pefindo for ${n} selected row${n === 1 ? '' : 's'}? Runs one at a time.`,
      })
    },
    bulkRetryAirpack() {
      return this.runBulkAction({
        action: 'bulk-airpack',
        label: 'Airpack retry',
        urlForRow: (row) => `/pending-submissions/${row.id}/retry-pefindo`,
        filterFn: (row) => this.canBulkAirpack(row),
        confirmText: (n) => `Retry Airpack for ${n} selected row${n === 1 ? '' : 's'}? Uses saved Pefindo; runs one at a time.`,
      })
    },
    bulkRescanPolicy() {
      return this.runBulkAction({
        action: 'bulk-rescan',
        label: 'P10–P40 rescan',
        urlForRow: (row) => `/pending-submissions/${row.id}/rescan-policy`,
        filterFn: (row) => this.canRescanPolicy(row),
        confirmText: (n) => `Rescan P10–P40 on saved Pefindo for ${n} selected row${n === 1 ? '' : 's'}? No Eagle/Airpack call.`,
      })
    },
    bulkCheckLos() {
      return this.runBulkAction({
        action: 'bulk-check-los',
        label: 'Check LOS',
        urlForRow: (row) => `/pending-submissions/${row.id}/check-los`,
        filterFn: (row) => this.canBulkCheckLos(row),
        confirmText: (n) => `Check LOS for ${n} selected row${n === 1 ? '' : 's'}? Rows already in LOS are removed from the queue.`,
      })
    },
    startRetry(row) {
      if (!this.canRetryPefindo(row) || this.busyId !== null) return
      const airpackOnly = row.pefindo_status === 'ready' && row.airpack_status === 'failed'
      const action = airpackOnly ? 'retry-airpack' : 'retry-pefindo'
      this.startAction(row, action, `/pending-submissions/${row.id}/retry-pefindo`)
    },
    confirmDismiss(row) {
      if (!window.confirm(`Dismiss pending #${row.id} for ${row.name || row.nik}?`)) return
      this.startAction(row, 'dismiss', `/pending-submissions/${row.id}/dismiss`)
    },
    async openSubmitPreview(row) {
      if (!this.canSubmitLos(row) || this.busyId !== null) return
      this.submitPreviewRow = row
      this.submitPreviewOpen = true
      this.submitPreviewLoading = true
      this.submitPreviewError = ''
      this.submitPreview = null
      try {
        const response = await fetch(`/pending-submissions/${row.id}/submit-los-preview`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-XSRF-TOKEN': this.xsrfToken(),
          },
        })
        const body = await response.json().catch(() => ({}))
        if (!response.ok || body.success === false) {
          this.submitPreviewError = body.error || body.message || 'Failed to load LOS preview'
          return
        }
        this.submitPreview = body
      } catch (error) {
        this.submitPreviewError = error.message || 'Failed to load LOS preview'
      } finally {
        this.submitPreviewLoading = false
      }
    },
    closeSubmitPreview() {
      if (this.busyAction === 'submit-los') return
      this.submitPreviewOpen = false
      this.submitPreviewLoading = false
      this.submitPreviewError = ''
      this.submitPreview = null
      this.submitPreviewRow = null
    },
    confirmSubmitLos() {
      const row = this.submitPreviewRow
      if (!row || this.busyId !== null) return
      this.submitPreviewOpen = false
      this.startAction(row, 'submit-los', `/pending-submissions/${row.id}/submit-los`)
      this.submitPreview = null
      this.submitPreviewRow = null
    },
    canRetryPefindo(row) {
      if (!['pending', 'ready_for_los'].includes(row.status)) return false
      // Airpack-only retry when Pefindo already ready (saved on park).
      if (row.pefindo_status === 'ready' && row.airpack_status === 'failed') return true
      // Continue an Eagle report that already has an event id.
      if (
        row.event_id &&
        (row.reason === 'PEFINDO_PENDING' ||
          row.reason === 'LIMIT_ERROR' ||
          row.pefindo_status === 'waiting' ||
          row.pefindo_status === 'failed')
      ) {
        return true
      }
      // Failed before Eagle created a report: search again from the saved application.
      return (
        !row.event_id &&
        row.pefindo_status === 'failed' &&
        !!(row.nik || (row.payload && row.payload.eKtpNum)) &&
        !!(row.name || (row.payload && row.payload.custNm))
      )
    },
    retryButtonLabel(row) {
      if (row.pefindo_status === 'ready' && row.airpack_status === 'failed') {
        return 'Retry Airpack'
      }
      if (!row.event_id && row.pefindo_status === 'failed') {
        return 'Search Pefindo'
      }
      return 'Retry Pefindo'
    },
    isPefindoNotFound(row) {
      if (!row) return false
      if (['submitted', 'dismissed', 'already_in_los'].includes(row.status)) return false
      if (row.pefindo_status === 'ready') return false
      if (row.payload && row.payload.rejectCode === 'P00') return true
      if (row.reason !== 'LIMIT_ERROR') return false
      const err = String(row.last_error || '').toLowerCase()
      return err.includes('no inquiry result') || err.includes('data not found')
    },
    isLimitReject(row) {
      if (!row || row.status !== 'ready_for_los') return false
      const code = this.rowRejectCode(row)
      if (code === 'LIMIT' || code === 'N10') return true
      if (row.payload && (row.payload.reject_reason === 'LIMIT' || row.payload.rejectCode === 'LIMIT')) return true
      return String(row.last_error || '').includes('submit as LIMIT reject') || String(row.last_error || '').includes('[LIMIT]')
    },
    canRescanPolicy(row) {
      if (!['pending', 'ready_for_los'].includes(row.status)) return false
      if (row.pefindo_status !== 'ready') return false
      // LIMIT (N10) rows that may actually be writeoff / past-due P10–P40.
      if (this.isLimitReject(row)) return true
      const err = String(row.last_error || '')
      return err.includes('[LIMIT]') || err.includes('below 3M') || err.includes('No limit')
    },
    submitLosLabel(row) {
      if (this.isPefindoNotFound(row)) return 'Submit reject P00'
      if (this.isLimitReject(row)) return 'Submit reject LIMIT'
      if (row.airpack_status === 'skipped' && row.last_error && String(row.last_error).includes('Policy reject')) {
        const m = String(row.last_error).match(/Policy reject\s+(\S+)/)
        return m ? `Submit reject ${m[1]}` : 'Submit policy reject'
      }
      return 'Submit to LOS'
    },
    canSubmitLos(row) {
      if (['submitted', 'dismissed', 'already_in_los'].includes(row.status)) return false
      // Eagle searched and returned no bureau hit — send LOS rejection P00.
      if (this.isPefindoNotFound(row)) return true
      // Only after retry produced ready Pefindo + Airpack (includes LIMIT reject below 3M).
      // airpack skipped = P10–P40 bureau reject without Airpack.
      if (row.status !== 'ready_for_los') return false
      if (row.airpack_status !== 'ready' && row.airpack_status !== 'skipped') return false
      if (row.pefindo_status !== 'ready') return false
      return true
    },
    statusClass(status) {
      if (status === 'ready_for_los') return 'bg-teal-50 text-teal-800'
      if (status === 'pending') return 'bg-amber-50 text-amber-800'
      if (status === 'submitted') return 'bg-green-50 text-green-800'
      if (status === 'already_in_los') return 'bg-blue-50 text-blue-800'
      if (status === 'dismissed') return 'bg-gray-100 text-gray-600'
      return 'bg-gray-100 text-gray-600'
    },
    statusLabel(status) {
      const map = {
        pending: 'Pending',
        ready_for_los: 'Ready for LOS',
        submitted: 'Submitted',
        already_in_los: 'Already in LOS',
        dismissed: 'Dismissed',
      }
      return map[status] || status || '—'
    },
    rowRejectCode(row) {
      const code = row.payload_reject_code || (row.payload && row.payload.rejectCode) || ''
      const reason = row.payload_reject_reason || (row.payload && row.payload.reject_reason) || ''
      if (code && code !== 'null') return String(code)
      if (reason && reason !== 'null') return String(reason)
      return ''
    },
    isApprovePass(row) {
      if (!row || row.status !== 'ready_for_los') return false
      if (row.pefindo_status !== 'ready') return false
      if (row.airpack_status !== 'ready' && row.airpack_status !== 'skipped') return false
      if (this.isPefindoNotFound(row) || this.isLimitReject(row)) return false
      if (this.rowRejectCode(row)) return false
      // Policy reject rows keep airpack skipped + reject in last_error / payload.
      if (row.airpack_status === 'skipped' && String(row.last_error || '').includes('Policy reject')) {
        return false
      }
      return true
    },
    categoryLabel(row) {
      if (this.isApprovePass(row)) return 'Pass'
      const reject = this.rowRejectCode(row)
      if (reject) return `Reject ${reject}`
      if (this.isPefindoNotFound(row)) return 'Reject P00'
      if (this.isLimitReject(row)) return 'Reject LIMIT'
      const map = {
        PEFINDO_PENDING: 'Slow Pefindo',
        PRE_LIMIT_ERROR: 'Pre-limit error',
        LIMIT_ERROR: 'Limit error',
        AIRPACK_PENDING: 'Airpack pending',
        LOS_SUBMIT_UNCERTAIN: 'LOS uncertain',
      }
      return map[row.reason] || row.reason || '—'
    },
    categoryBadgeClass(row) {
      if (this.isApprovePass(row)) return 'bg-green-50 text-green-800'
      if (this.rowRejectCode(row) || this.isPefindoNotFound(row) || this.isLimitReject(row)) {
        return 'bg-red-50 text-red-800'
      }
      if (row.reason === 'PEFINDO_PENDING') return 'bg-amber-50 text-amber-800'
      if (row.reason === 'PRE_LIMIT_ERROR' || row.reason === 'LIMIT_ERROR') return 'bg-red-50 text-red-800'
      if (row.reason === 'AIRPACK_PENDING') return 'bg-purple-50 text-purple-800'
      if (row.reason === 'LOS_SUBMIT_UNCERTAIN') return 'bg-amber-50 text-amber-800'
      return 'bg-gray-100 text-gray-700'
    },
    reasonLabel(reason) {
      const map = {
        PEFINDO_PENDING: 'Slow Pefindo',
        PRE_LIMIT_ERROR: 'Pre-limit error',
        LIMIT_ERROR: 'Limit error',
        AIRPACK_PENDING: 'Airpack pending',
        LOS_SUBMIT_UNCERTAIN: 'LOS uncertain',
      }
      return map[reason] || reason || '—'
    },
    reasonBadgeClass(reason) {
      if (reason === 'PEFINDO_PENDING') return 'bg-amber-50 text-amber-800'
      if (reason === 'PRE_LIMIT_ERROR' || reason === 'LIMIT_ERROR') return 'bg-red-50 text-red-800'
      if (reason === 'AIRPACK_PENDING') return 'bg-purple-50 text-purple-800'
      return 'bg-gray-100 text-gray-700'
    },
    formatDate(value) {
      if (!value) return '—'
      return moment(value).format('DD MMM YYYY HH:mm')
    },
    formatNumber(value) {
      return new Intl.NumberFormat('id-ID').format(Number(value) || 0)
    },
    rowNumber(index) {
      const from = this.rows.from || ((this.rows.current_page - 1) * (this.rows.per_page || 15) + 1)
      return from + index
    },
  },
}
</script>
